import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { listCapEndpoints, selector as listCapSelector } from './listCapSlice';
import { updateCapEndpoint, selector as updateCapSelector } from '../updateCapEndpointSlice';
import { deleteCapEndpoint, selector as deleteCapSelector } from '../deleteCapEndpointSlice';
import { regenerateCapEndpointToken, selector as regenerateCapSelector } from '../regenerateCapEndpointTokenSlice';
import { DataAvailabilityMessages, Status } from '../../../../common/constants';
import { capWebhookURL } from '../../../../config';
import useNotification from '../../../../components/notification/useNotification';
import { useSpinner } from '../../../../customHooks';
import { ICapEndpointRow } from '../capTypes';
import { ITemplate } from '../../../../common/types/templateTypes';
import {
  getOrgTemplates,
  getTemplates,
  mergeOrgTemplates,
} from '../../../../components/superAdmin/templatesSlice';
import { getCapOrgId } from '../capUtils';

const useListCap = () => {
  const dispatch: any = useDispatch();
  const { showSuccessNotification, showWarningNotification } = useNotification();
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [templatesById, setTemplatesById] = useState<Map<number, ITemplate>>(
    () => new Map()
  );

  const apiState = useSelector(listCapSelector);
  const endpoints = apiState?.endpoints ?? [];
  const apiStatus: string = apiState?.status ?? Status.IDLE;
  useSpinner(apiStatus);
  useSelector(updateCapSelector);
  useSelector(deleteCapSelector);
  useSelector(regenerateCapSelector);

  useEffect(() => {
    dispatch(listCapEndpoints());
  }, [dispatch]);

  useEffect(() => {
    const fetchTemplates = async () => {
      const orgId = getCapOrgId();
      if (!orgId) return;
      const [orgResult, publicResult] = await Promise.all([
        dispatch(getOrgTemplates(orgId)),
        dispatch(getTemplates({ public: true })),
      ]);
      const publicList = getTemplates.fulfilled.match(publicResult)
        ? publicResult.payload
        : [];
      const orgList = getOrgTemplates.fulfilled.match(orgResult)
        ? orgResult.payload
        : [];
      const merged = mergeOrgTemplates(publicList, orgList);
      setTemplatesById(new Map(merged.map((template) => [template.templateId, template])));
    };
    fetchTemplates();
  }, [dispatch]);

  const handleCopyUrl = (endpointToken: string, id: string) => {
    navigator.clipboard.writeText(`${capWebhookURL}${endpointToken}`);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleToggleActive = async (endpointId: string, isActive: boolean) => {
    const response = await dispatch(updateCapEndpoint({ endpointId, isActive: !isActive }));
    if (response?.meta?.requestStatus === 'fulfilled') {
      showSuccessNotification(`Endpoint ${!isActive ? 'activated' : 'deactivated'} successfully`);
      dispatch(listCapEndpoints());
    } else {
      showWarningNotification('Failed to update endpoint');
    }
  };

  const handleDelete = async (endpointId: string) => {
    const response = await dispatch(deleteCapEndpoint(endpointId));
    if (response?.meta?.requestStatus === 'fulfilled') {
      showSuccessNotification('CAP endpoint deleted successfully');
      dispatch(listCapEndpoints());
    } else {
      showWarningNotification('Failed to delete endpoint');
    }
  };

  const handleRegenerateToken = async (endpointId: string): Promise<string | null> => {
    const response = await dispatch(regenerateCapEndpointToken(endpointId));
    if (response?.meta?.requestStatus === 'fulfilled') {
      const endpoint = response.payload?.message;
      showSuccessNotification('CAP endpoint token regenerated successfully');
      dispatch(listCapEndpoints());
      return `${capWebhookURL}${endpoint?.endpointToken}`;
    }
    showWarningNotification(
      response?.payload?.response?.data?.message || 'Failed to regenerate token'
    );
    return null;
  };

  const rows: ICapEndpointRow[] = useMemo(
    () =>
      endpoints.map((endpoint: any) => ({
        id: endpoint._id,
        name: endpoint.name,
        groups: endpoint.groups ?? [],
        templateId: endpoint.templateId,
        endpointToken: endpoint.endpointToken,
        isActive: endpoint.isActive,
        createdAt: endpoint.createdAt ? new Date(endpoint.createdAt).toLocaleString() : '',
      })),
    [endpoints]
  );

  const filteredRows = useMemo(() => {
    if (!globalFilter.trim()) return rows;
    return rows.filter((row) => row.name.toLowerCase().includes(globalFilter.trim().toLowerCase()));
  }, [globalFilter, rows]);

  const noDataMessage =
    apiStatus === Status.PENDING
      ? DataAvailabilityMessages.TOKEN_DATA_LOADING
      : 'No CAP endpoints configured.';

  return {
    rows: filteredRows,
    globalFilter,
    setGlobalFilter,
    noDataMessage,
    handleCopyUrl,
    copiedId,
    handleToggleActive,
    handleDelete,
    handleRegenerateToken,
    templatesById,
  };
};

export default useListCap;
